import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { 
  DataGrid, 
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridRenderCellParams
} from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { useLedgerMovements, LedgerMovement } from 'store/registry/useLedgerMovements';
import intl from 'components/i18n/ReactIntlWrapper';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

interface MovementsTableProps {
  accountId: number;
  accountCurrency?: string;
  onNewMovement?: () => void;
  onEditMovement?: (movement: LedgerMovement) => void;
  onDeleteMovement?: (movement: LedgerMovement) => void;
  onImportCsv?: () => void;
  onLinkMovement?: (movement: LedgerMovement) => void;
  onReconcileMovement?: (movement: LedgerMovement) => void;
}

const MovementsTable: React.FC<MovementsTableProps> = ({
  accountId,
  accountCurrency = 'USD',
  onNewMovement,
  onEditMovement,
  onDeleteMovement,
  onImportCsv,
  onLinkMovement,
  onReconcileMovement,
}) => {
  // Keep page separate so we can reset it atomically with filter changes
  const [page, setPage] = useState(0);
  const [paginationPreference, setPaginationPreference] = usePaginationPreference('movements', 10);
  const pageSize = paginationPreference.pageSize;
  
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'date', sort: 'desc' }
  ]);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const paginationModel: GridPaginationModel = { page, pageSize };

  // Fetch movements data
  const { 
    data: movementsData, 
    isLoading, 
    isFetching,
    error, 
    refetch 
  } = useLedgerMovements(accountId, {
    pageNum: page + 1,        // DataGrid is 0-based, API is 1-based
    pageSize,
    sort: sortModel[0]?.field || 'date',
    order: sortModel[0]?.sort || 'desc',
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  // Format currency without signs (using colors instead)
  const formatCurrency = useCallback((amount: number, currencyCode = accountCurrency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(Math.abs(amount)); // Use absolute value, colors show the sign
    } catch (error) {
      // Fallback for invalid currency codes
      return `${currencyCode} ${Math.abs(amount).toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2
      })}`;
    }
  }, [accountCurrency]);

  // Get amount color based on credit/debit
  const getAmountColor = useCallback((movement: LedgerMovement) => {
    return movement.type === 'credit' ? 'success.main' : 'error.main';
  }, []);



  // Define DataGrid columns
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'date',
      headerName: intl.get('movements.date'),
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<LedgerMovement>) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value ? new Date(params.value).toLocaleDateString() : ''}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: intl.get('accountManagement.description'),
      flex: 2.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<LedgerMovement>) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'displayAmount',
      headerName: intl.get('accountManagement.amount'),
      flex: 1.2,
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<LedgerMovement>) => (
        <Typography 
          variant="body2"
          color={getAmountColor(params.row)}
          fontWeight="600"
          fontFamily="monospace"
        >
          {formatCurrency(params.value, params.row.currency?.code || 'USD')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      disableColumnMenu: true,

      renderCell: (params: GridRenderCellParams<LedgerMovement>) => {
        const isLinked = params.row.movementType === 'linked';
        return (
          <Box display="flex" gap={0.5} alignItems="center" height="100%">
            {onReconcileMovement && (params.row.type === 'credit' || params.row.type === 'debit') && (
              <PermissionsGate identifier="ui.accounts.movements.reconcile-button" entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
                <Tooltip title={
                  params.row.type === 'credit'
                    ? (intl.get('reconciliation.reconcileEntry') || 'Reconcile with issued invoices')
                    : (intl.get('reconciliation.reconcileApEntry') || 'Reconcile with AP invoices')
                }>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={(e) => { e.stopPropagation(); onReconcileMovement(params.row); }}
                  >
                    <Icon icon="mdi:bank-check" width={18} />
                  </IconButton>
                </Tooltip>
              </PermissionsGate>
            )}
            {onLinkMovement && (
              <PermissionsGate identifier="ui.accounts.movements.edit-button" entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
                <Tooltip title={isLinked
                  ? (intl.get('ledgerLink.viewLink') || 'View link')
                  : (intl.get('ledgerLink.linkEntry') || 'Link to another entry')
                }>
                  <IconButton
                    size="small"
                    color={isLinked ? 'primary' : 'default'}
                    onClick={(e) => { e.stopPropagation(); onLinkMovement(params.row); }}
                  >
                    <Icon icon={isLinked ? 'mdi:link-variant' : 'mdi:link-variant-plus'} width={18} />
                  </IconButton>
                </Tooltip>
              </PermissionsGate>
            )}
            {onEditMovement && (
              <PermissionsGate identifier="ui.accounts.movements.edit-button" entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
                <Tooltip title={intl.get('common.edit')}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onEditMovement(params.row); }}
                  >
                    <Icon icon="mdi:pencil" width={18} />
                  </IconButton>
                </Tooltip>
              </PermissionsGate>
            )}
            {onDeleteMovement && (
              <PermissionsGate identifier="ui.accounts.movements.delete-button" entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
                <Tooltip title={intl.get('common.delete')}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); onDeleteMovement(params.row); }}
                  >
                    <Icon icon="mdi:trash-can-outline" width={18} />
                  </IconButton>
                </Tooltip>
              </PermissionsGate>
            )}
          </Box>
        );
      },
    },
  ], [formatCurrency, getAmountColor, onReconcileMovement, onLinkMovement, onEditMovement, onDeleteMovement]);

  // Handle pagination change
  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPage(newModel.page);
    setPaginationPreference({ page: 0, pageSize: newModel.pageSize });
  }, [setPaginationPreference]);

  // Handle sort change
  const handleSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel);
    setPage(0); // reset to first page on sort change
  }, []);

  const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setPage(0);
  }, []);

  const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setPage(0);
  }, []);

  const handleClearDates = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setPage(0);
  }, []);

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => refetch()}
                startIcon={<Icon icon="mdi:refresh" />}
              >
                {intl.get('movements.retry')}
              </Button>
            }
          >
            {intl.get('movements.errorLoading')}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:format-list-bulleted" width={20} />
            {intl.get('accountDetail.movements')}
          </Typography>
          <Box display="flex" gap={1}>
            {onImportCsv && (
              <PermissionsGate identifier="ui.accounts.movements.add-button" entityType={ENTITY_TYPE.CORPORATE_ENTITY}>
                <Button
                  variant="outlined"
                  startIcon={<Icon icon="mdi:file-import-outline" />}
                  onClick={onImportCsv}
                >
                  {intl.get('csvImport.buttonLabel')}
                </Button>
              </PermissionsGate>
            )}
            {onNewMovement && (
              <Button
                variant="contained"
                startIcon={<Icon icon="mdi:plus" />}
                onClick={onNewMovement}
              >
                {intl.get('accountDetail.newMovement')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Date filters */}
        <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
          <TextField
            label={intl.get('movements.filter.startDate')}
            type="date"
            size="small"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 180 }}
          />
          <TextField
            label={intl.get('movements.filter.endDate')}
            type="date"
            size="small"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 180 }}
          />
          {(startDate || endDate) && (
            <Tooltip title={intl.get('movements.filter.clearDates')}>
              <IconButton size="small" onClick={handleClearDates}>
                <Icon icon="mdi:close-circle-outline" width={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box height={400}>
          <DataGrid
            rows={movementsData?.data || []}
            columns={columns}
            loading={isLoading || isFetching}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            paginationMode="server"
            sortingMode="server"
            rowCount={movementsData?.totalRecords ?? 0}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            localeText={{
              noRowsLabel: intl.get('movements.noResults'),
            }}
            slotProps={{
              pagination: {
                labelRowsPerPage: intl.get('movements.pageSize'),
              },
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                border: 'none',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                borderBottom: 2,
                borderColor: 'grey.200',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'grey.50',
                },
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default MovementsTable;